import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BadgeCheckIcon, ClockIcon, FlameIcon, HeadsetIcon, MapPinIcon, SearchIcon, ShieldCheckIcon, SparklesIcon, TagIcon, UsersIcon, WalletIcon } from 'lucide-react';
import { ItemCard } from '../../../components/market/ItemCard';
import { CategoryCard } from '../../../components/market/CategoryCard';
import { MiniListing } from '../../../components/market/MiniListing';
import { ProviderCard } from '../../../components/market/ProviderCard';
import { DealBanner } from '../../../components/market/DealBanner';
import { RecentCard } from '../../../components/market/RecentCard';
import { MarketRail } from '../../../components/market/MarketRail';
import { useAccount } from '../../../contexts/AccountContext';
import { marketCategories, marketItems, marketSellers } from '../../../data/market';
import { marketDeals, marketHeroImage, providerCtaImage, trustPoints } from '../../../data/marketVisual';
import { timetable } from '../../../data/academics';
const FILTERS = [{
  id: 'All',
  icon: SparklesIcon,
  note: 'Everything offered around your campus.'
}, {
  id: 'Nearby',
  icon: MapPinIcon,
  note: 'Ordered by distance from your campus.'
}, {
  id: 'Popular',
  icon: FlameIcon,
  note: 'Ordered by completed orders, not by how often a listing was viewed.'
}, {
  id: 'Verified',
  icon: BadgeCheckIcon,
  note: 'Only sellers Campuzon has inspected. Verification is not a guarantee that a transaction will go well.'
}, {
  id: 'New',
  icon: ClockIcon,
  note: 'Listed in the last 14 days.'
}, {
  id: 'Deals',
  icon: TagIcon,
  note: 'Listings in a campaign that has a stated end date.'
}] as const;
const TRUST_ICONS = {
  verified: ShieldCheckIcon,
  student: UsersIcon,
  payments: WalletIcon,
  support: HeadsetIcon
} as const;
export function MarketHome() {
  const {
    campusName,
    institutionName,
    account
  } = useAccount();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['id']>('All');

  /** Recommendation signal: the courses you are actually timetabled for. */
  const registeredCourses = useMemo(() => Array.from(new Set(timetable.map(session => session.courseCode))), []);
  const recommended = useMemo(() => marketItems.filter(item => item.courseCodes?.some(code => registeredCourses.includes(code))).slice(0, 3), [registeredCourses]);
  const popular = useMemo(() => [...marketItems].filter(item => item.rating !== undefined).sort((a, b) => (b.reviewCount ?? 0) - (a.reviewCount ?? 0)).slice(0, 3), []);

  /** Stands in for the browsing history a signed-in account accumulates. */
  const recentlyViewed = useMemo(() => ['itm-a1', 'itm-b2', 'itm-f1', 'itm-s1', 'itm-g1'].map(id => marketItems.find(item => item.id === id)).filter((item): item is NonNullable<typeof item> => Boolean(item)), []);
  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return [];
    return marketItems.filter(item => item.title.toLowerCase().includes(term) || item.summary.toLowerCase().includes(term) || item.tags?.some(tag => tag.toLowerCase().includes(term)));
  }, [query]);
  const verifiedProviders = marketSellers.filter(seller => seller.verified);
  const activeFilter = FILTERS.find(entry => entry.id === filter);
  return <div className="grid gap-8 xl:grid-cols-[220px_minmax(0,1fr)] 2xl:grid-cols-[220px_minmax(0,1fr)_320px]" data-id="element-10738" data-mp="owmlzzxbtyf5">
      {/* Marketplace navigation — desktop only; the shell carries it on mobile. */}
      <aside className="hidden xl:block" data-id="element-10739" data-mp="xycdt6v4ezdg">
        <div className="sticky top-24" data-id="element-10740" data-mp="24cv63uv5m123">
          <MarketRail data-id="element-10741" data-mp="24btlozs9027p" />
        </div>
      </aside>

      <div className="min-w-0 space-y-10" data-id="element-10742" data-mp="24f7p8bcnehgq">
        {/* ------------------------------------------------------------- hero */}
        <section className="overflow-hidden rounded-panel border border-line bg-surface" data-id="element-10743" data-mp="27u957u3r4c18">
          <div className="grid items-center gap-6 px-5 py-7 sm:px-8 sm:py-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]" data-id="element-10744" data-mp="22vxxpq0xidm6">
            <div className="min-w-0" data-id="element-10745" data-mp="2gwid0c7mxrax">
              <h1 className="text-h1 font-semibold tracking-tight text-ink lg:text-display" data-id="element-10746" data-mp="2gtcq93mhb1id">
                Campus Marketplace
              </h1>
              <p className="mt-3 text-body-lg font-medium text-ink" data-id="element-10747" data-mp="2qsv7hcb0896q">
                Everything you need around campus.
              </p>
              <p className="mt-2 max-w-[52ch] text-body text-ink-muted" data-id="element-10748" data-mp="2lua4x68a5df4">
                Buy, book, hire and discover trusted products and services from your campus
                community.
              </p>

              <p className="mt-5 inline-flex min-h-touch items-center gap-2 rounded-lg border border-line bg-canvas px-3.5 text-body-sm font-medium text-ink" data-id="element-10749" data-mp="2vmg7tb02lf93">
                <MapPinIcon aria-hidden className="h-4 w-4 text-brand-700" data-id="element-10750" data-mp="34m2x4yfiyuls" />
                {campusName} · {institutionName}
              </p>
            </div>

            <img src={marketHeroImage} alt="" className="mx-auto hidden h-56 w-full max-w-sm object-contain lg:block" data-id="element-10751" data-mp="3t5wit03mh38z" />
          </div>

          {/* --------------------------------------------------------- search */}
          <div className="border-t border-line bg-canvas px-5 py-5 sm:px-8" data-id="element-10752" data-mp="3j9tkk5bw5orb">
            <label htmlFor="market-search" className="text-body font-semibold text-ink" data-id="element-10753" data-mp="35fk6ze59amd4">
              What are you looking for?
            </label>
            <div className="mt-2.5 flex flex-col gap-2.5 sm:flex-row" data-id="element-10754" data-mp="2vi5xjyhr2xub">
              <div className="relative flex-1" data-id="element-10755" data-mp="2vg30wvm3e6gm">
                <SearchIcon aria-hidden className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-subtle" data-id="element-10756" data-mp="30ghawj797qha" />
                <input id="market-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="E.g. fried rice, engineering textbook, apartment, AutoCAD service…" className="min-h-touch-lg w-full rounded-lg border border-line bg-surface py-3 pl-11 pr-3 text-body text-ink shadow-[0_1px_2px_rgba(15,23,42,0.04)] placeholder:text-ink-subtle" data-id="element-10757" data-mp="2rh2k1in6ddw9" />
              </div>
              <button type="button" className="inline-flex min-h-touch-lg items-center justify-center rounded-lg border border-brand-700 bg-brand-700 px-7 text-body font-medium text-white transition-colors duration-150 ease-out hover:border-brand-800 hover:bg-brand-800 hover:text-white" data-id="element-10758" data-mp="1fxx0kzoz9n8b">
                Search
              </button>
            </div>

            <div className="cz-tabstrip mt-3 flex min-w-0 gap-2" data-id="element-10759" data-mp="3rpisqd2ym67m">
              {FILTERS.map(entry => {
              const Icon = entry.icon;
              const active = filter === entry.id;
              return <button key={entry.id} type="button" onClick={() => setFilter(entry.id)} aria-pressed={active} className={`inline-flex min-h-touch shrink-0 items-center gap-2 rounded-full border px-4 text-body-sm font-medium transition-colors duration-150 ease-out ${active ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-line bg-surface text-ink-muted hover:border-line-strong hover:text-ink'}`} data-id="element-10760" data-mp="225iw6y6rrqeo">
                    <Icon aria-hidden className="h-4 w-4" data-id="element-10761" data-mp="2yxw7zorr77fw" />
                    {entry.id}
                  </button>;
            })}
            </div>

            {activeFilter && <p className="mt-2.5 text-caption text-ink-muted" data-id="element-10762" data-mp="14jvd1ezbr4l3">{activeFilter.note}</p>}
          </div>
        </section>

        {/* ------------------------------------------------------------ search */}
        {query.trim() && <section aria-labelledby="market-results" data-id="element-10763" data-mp="gnqlgjfid10u">
            <h2 id="market-results" className="text-h3 font-semibold text-ink" data-id="element-10764" data-mp="ufwznzz3oxxn">
              {results.length} {results.length === 1 ? 'result' : 'results'} for “{query.trim()}”
            </h2>
            {results.length === 0 ? <p className="mt-2 max-w-[60ch] text-body text-ink-muted" data-id="element-10765" data-mp="2ps9tlsknip1">
                Nothing around {campusName} matches that yet. Try a broader term, or browse a
                category below — Campuzon will not invent listings to fill a search.
              </p> : <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-10766" data-mp="6q2pxra2vphm">
                {results.map(item => {
            const category = marketCategories.find(entry => entry.id === item.category);
            return <ItemCard key={item.id} item={item} pattern={category?.pattern ?? 'goods'} data-id="element-10767" data-mp="1i594yorb4luo" />;
          })}
              </div>}
          </section>}

        {/* -------------------------------------------------------- categories */}
        <section aria-labelledby="market-categories" data-id="element-10768" data-mp="1gpn8x84lfehh">
          <h2 id="market-categories" className="text-h3 font-semibold text-ink" data-id="element-10769" data-mp="1ln6nk957dt9y">
            Explore by category
          </h2>
          <p className="mt-1.5 max-w-[64ch] text-body text-ink-muted" data-id="element-10770" data-mp="29e2c8xed48cz">
            Each category has its own experience. Ordering food is not the same as buying a used
            textbook or applying for a room, so they do not share one listing template.
          </p>
          <ul className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-3" data-id="element-10771" data-mp="24m167t7sfdve">
            {marketCategories.map(category => <li key={category.id} data-id="element-10772" data-mp="2sblrwjvbsp8d">
                <CategoryCard category={category} data-id="element-10773" data-mp="2sakdc5f3pklw" />
              </li>)}
          </ul>
        </section>

        {/* --------------------------------------------------------- providers */}
        <section aria-labelledby="market-providers" data-id="element-10774" data-mp="drahn4u911i5">
          <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-10775" data-mp="ipu1mzpa837v">
            <h2 id="market-providers" className="text-h3 font-semibold text-ink" data-id="element-10776" data-mp="4q2r58xz54ym">
              Verified providers you can trust
            </h2>
            <Link to="/market/services" className="inline-flex min-h-touch items-center gap-1.5 text-body-sm font-medium text-brand-700 hover:text-brand-800" data-id="element-10777" data-mp="9ncdugtzb2ir">
              View all
              <ArrowRightIcon aria-hidden className="h-4 w-4" data-id="element-10778" data-mp="3vy6widq4kdus" />
            </Link>
          </div>
          <p className="mt-1.5 max-w-[66ch] text-body-sm text-ink-muted" data-id="element-10779" data-mp="3i2medki7zzof">
            Verified means Campuzon has checked who this provider is. It is not a promise about the
            quality of any particular order, and it is not a guarantee of a refund.
          </p>
          <ul className="cz-tabstrip mt-4 flex min-w-0 gap-4" data-id="element-10780" data-mp="3d4b5pd3nvv7s">
            {verifiedProviders.map(seller => <li key={seller.id} className="w-[16.5rem] shrink-0" data-id="element-10781" data-mp="2tk2qe6x2cx7c">
                <ProviderCard seller={seller} data-id="element-10782" data-mp="2olsufu3ilsko" />
              </li>)}
          </ul>
        </section>

        {/* ------------------------------------------------------------- deals */}
        <section aria-labelledby="market-deals" data-id="element-10783" data-mp="2yhvwz12a3t85">
          <h2 id="market-deals" className="text-h3 font-semibold text-ink" data-id="element-10784" data-mp="2yla0wdqlcahp">
            Deals and offers
          </h2>
          <p className="mt-1.5 text-body-sm text-ink-muted" data-id="element-10785" data-mp="37jvdls9dsvak">
            Every campaign states when it ends. A discount with no end date is not a discount.
          </p>
          <div className="mt-4 grid gap-4 lg:grid-cols-3" data-id="element-10786" data-mp="3makr51abemhl">
            {marketDeals.map(deal => <DealBanner key={deal.id} deal={deal} data-id="element-10787" data-mp="3hbhvg7g85sma" />)}
          </div>
        </section>

        {/* --------------------------------------------------- recently viewed */}
        <section aria-labelledby="market-recent" data-id="element-10788" data-mp="3r9fk2w1zxrff">
          <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-10789" data-mp="25wxoitkuj48">
            <h2 id="market-recent" className="text-h3 font-semibold text-ink" data-id="element-10790" data-mp="74hs21m5e8kr">
              Recently viewed
            </h2>
            <p className="text-caption text-ink-subtle" data-id="element-10791" data-mp="c3b21k287hg6">Only you can see this.</p>
          </div>
          <ul className="cz-tabstrip mt-4 flex min-w-0 gap-4" data-id="element-10792" data-mp="gujdeln49cvz">
            {recentlyViewed.map(item => <li key={item.id} className="shrink-0" data-id="element-10793" data-mp="kuuy1yfhbd7g">
                <RecentCard item={item} data-id="element-10794" data-mp="puzexpkau9y7" />
              </li>)}
          </ul>
        </section>

        {/* ------------------------------------------------------------- trust */}
        <section aria-label="What Campuzon stands behind" data-id="element-10795" data-mp="us7ynul3im1k">
          <ul className="grid gap-5 rounded-panel border border-line bg-surface px-5 py-6 sm:grid-cols-2 sm:px-7 lg:grid-cols-4" data-id="element-10796" data-mp="zrud27cnu8by">
            {trustPoints.map(point => {
            const Icon = TRUST_ICONS[point.id];
            return <li key={point.id} className="flex gap-3" data-id="element-10797" data-mp="1ipdzqq6a541f">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50" data-id="element-10798" data-mp="1iqyvuk88sv1e">
                    <Icon aria-hidden className="h-5 w-5 text-brand-700" data-id="element-10799" data-mp="1dxe2x6liar92" />
                  </span>
                  <div className="min-w-0" data-id="element-10800" data-mp="19xc6bbumoo0n">
                    <p className="text-body-sm font-semibold text-ink" data-id="element-10801" data-mp="14yiz215xpt8x">{point.label}</p>
                    <p className="mt-1 text-caption leading-5 text-ink-muted" data-id="element-10802" data-mp="21o00u51sqv5c">{point.detail}</p>
                  </div>
                </li>;
          })}
          </ul>
        </section>

        {/* ------------------------------------------------------ provider CTA */}
        <section className="relative overflow-hidden rounded-panel bg-brand-800" data-id="element-10803" data-mp="1spekmgjsrs7q">
          <img src={providerCtaImage} alt="" loading="lazy" className="absolute inset-y-0 right-0 hidden h-full w-1/2 object-cover sm:block" data-id="element-10804" data-mp="1nql9gurzgdaq" />
          <span aria-hidden className="absolute inset-0 hidden bg-gradient-to-r from-brand-800 via-brand-800/95 to-transparent sm:block" data-id="element-10805" data-mp="cbes3qna74ui" />
          <div className="relative max-w-[38rem] px-5 py-8 sm:px-8 sm:py-10" data-id="element-10806" data-mp="24xjfb30kipi4">
            <h2 className="text-h2 font-semibold tracking-tight text-white" data-id="element-10807" data-mp="1r64podc5nq23">
              Have something to sell or a service to offer?
            </h2>
            <p className="mt-2.5 text-body-lg text-brand-50" data-id="element-10808" data-mp="1w222was4ggan">
              Reach thousands of students on your campus. You keep one account — the provider role
              is added to it, and what you must verify depends on what you offer.
            </p>
            <Link to="/market/sell" className="mt-6 inline-flex min-h-touch-lg items-center justify-center rounded-lg bg-white px-6 text-body font-semibold text-brand-800 transition-colors duration-150 ease-out hover:bg-brand-50" data-id="element-10809" data-mp="29v1gg78cg0fk">
              Become a provider
            </Link>
          </div>
        </section>
      </div>

      {/* --------------------------------------------------------- right rail */}
      <aside className="min-w-0 space-y-6 2xl:sticky 2xl:top-24 2xl:self-start" data-id="element-10810" data-mp="qx578pgvjfns">
        <section aria-labelledby="rail-popular" className="rounded-card border border-line bg-surface px-4 py-4" data-id="element-10811" data-mp="qz99z431e8mh">
          <div className="flex items-baseline justify-between gap-2" data-id="element-10812" data-mp="zxl95uhyyeqj">
            <h2 id="rail-popular" className="text-body font-semibold text-ink" data-id="element-10813" data-mp="c88sk9kbb4e7">
              Popular around {campusName}
            </h2>
          </div>
          <p className="mt-1 text-caption text-ink-subtle" data-id="element-10814" data-mp="87yox3jzsq2i">
            Ordered by completed orders, not views.
          </p>
          <ul className="mt-3 space-y-1" data-id="element-10815" data-mp="m0534k3l4mzb">
            {popular.map(item => <MiniListing key={item.id} item={item} data-id="element-10816" data-mp="h5sdttffghfn" />)}
          </ul>
        </section>

        {recommended.length > 0 && <section aria-labelledby="rail-recommended" className="rounded-card border border-line bg-surface px-4 py-4" data-id="element-10817" data-mp="3kobzgd3lop1h">
            <h2 id="rail-recommended" className="text-body font-semibold text-ink" data-id="element-10818" data-mp="1oke92inrwnjv">
              Recommended for you
            </h2>
            <p className="mt-1 text-caption leading-5 text-ink-muted" data-id="element-10819" data-mp="1yaqsg7juqaof">
              Because you are timetabled for {registeredCourses.slice(0, 3).join(', ')} on{' '}
              {account.academic.programme}.
            </p>
            <ul className="mt-3 space-y-1" data-id="element-10820" data-mp="15muyhalo2wvc">
              {recommended.map(item => <MiniListing key={item.id} item={item} reason={item.courseCodes?.join(', ')} data-id="element-10821" data-mp="1akmou06nyaxf" />)}
            </ul>
            <Link to="/settings" className="mt-3 inline-flex min-h-touch items-center text-caption font-medium text-brand-700 hover:text-brand-800" data-id="element-10822" data-mp="1jmcg960jp9v7">
              Turn personalisation off in Settings →
            </Link>
          </section>}

        <section aria-labelledby="rail-safety" className="rounded-card border border-line bg-surface px-4 py-4" data-id="element-10823" data-mp="jdeh5vks4xju">
          <h2 id="rail-safety" className="text-body font-semibold text-ink" data-id="element-10824" data-mp="fdcbzcfu5lzt">
            Stay safe on Campuzon
          </h2>
          <ul className="mt-3 space-y-3.5" data-id="element-10825" data-mp="aj850t6kldyq">
            {[{
            label: 'Use in-app messaging',
            detail: 'A dispute can only be reviewed from records Campuzon holds. Moving to WhatsApp removes that.'
          }, {
            label: 'Avoid off-platform payments',
            detail: 'No provider may ask you for an inspection fee, a deposit by transfer, or your bank details.'
          }, {
            label: 'Report suspicious activity',
            detail: 'Every listing, seller and order carries a report control that reaches a person.'
          }].map(tip => <li key={tip.label} data-id="element-10826" data-mp="pwarznwcma8b">
                <p className="text-body-sm font-semibold text-ink" data-id="element-10827" data-mp="px3rc3sz9xgf">{tip.label}</p>
                <p className="mt-0.5 text-caption leading-5 text-ink-muted" data-id="element-10828" data-mp="uvf00b7je1x2">{tip.detail}</p>
              </li>)}
          </ul>
          <Link to="/support" className="mt-3.5 inline-flex min-h-touch items-center gap-1.5 text-body-sm font-medium text-brand-700 hover:text-brand-800" data-id="element-10829" data-mp="g5pts1kl77px">
            Learn more about safety
            <ArrowRightIcon aria-hidden className="h-4 w-4" data-id="element-10830" data-mp="77xcwsz7vs0f" />
          </Link>
        </section>
      </aside>
    </div>;
}